import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useUI } from '../contexts/UIContext';
import { safeStorage } from '../utils/storage';

const INSTALL_DISMISSED_KEY = 'pwa_install_dismissed_at';
const INSTALL_DISMISS_TTL_DAYS = 14;

/**
 * Renders two PWA-related UX pieces:
 *
 *   1. An "Install app" banner that appears when the browser fires
 *      `beforeinstallprompt`. Dismissed banners hide for 14 days.
 *
 *   2. An "Update available" toast that fires when a new service worker
 *      is waiting. Clicking "Actualizar" reloads the page with the new
 *      version active.
 */
export default function PWAPrompts() {
  const { showNotification } = useUI();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // ───── Update prompt (new SW available) ─────
  // The hourly poll lives in a ref so we can clear it on unmount.
  // Without this, every PWAPrompts mount leaks an interval.
  const updateIntervalRef = useState({ current: null })[0];

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_, r) {
      // Check for updates every hour while the tab is open
      if (r && !updateIntervalRef.current) {
        updateIntervalRef.current = setInterval(() => r.update(), 60 * 60 * 1000);
      }
    },
  });

  // Clean up the SW update poller on unmount
  useEffect(() => () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
  }, [updateIntervalRef]);

  useEffect(() => {
    if (!needRefresh) return;
    // Con registerType='autoUpdate' + skipWaiting el SW nuevo ya toma
    // control automáticamente. Mostramos una notificación pasiva
    // (sin acción) avisando que en el próximo refresh queda activa.
    // No forzamos reload acá porque puede interrumpir una operación
    // in-flight (editar perfil, pegar lámina). El próximo navigate
    // natural ya servirá el bundle nuevo.
    showNotification({
      message: '✨ Nueva versión cargada. Se activa al refrescar.',
      type: 'info',
      action: {
        label: 'Refrescar ahora',
        onClick: () => window.location.reload(),
      },
    });
    setNeedRefresh(false);
  }, [needRefresh, showNotification, setNeedRefresh, updateServiceWorker]);

  // ───── Install prompt (beforeinstallprompt) ─────
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);

      // Show banner only if user hasn't dismissed recently
      const dismissedAt = parseInt(safeStorage.get(INSTALL_DISMISSED_KEY) || '0', 10);
      const ageMs = Date.now() - dismissedAt;
      if (ageMs > INSTALL_DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) {
        // Delay 5s so we don't compete with the onboarding modal / tour
        setTimeout(() => setShowInstallBanner(true), 5000);
      }
    };
    const onInstalled = () => {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    setShowInstallBanner(false);
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch {}
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    safeStorage.set(INSTALL_DISMISSED_KEY, String(Date.now()));
  };

  if (!showInstallBanner) return null;

  return (
    <div className="pwa-install-banner" role="dialog" aria-label="Instalar aplicación">
      <div className="pwa-install-icon">📱</div>
      <div className="pwa-install-body">
        <strong>Instala el Álbum 2026</strong>
        <span>Acceso rápido desde tu pantalla de inicio. Funciona sin internet.</span>
      </div>
      <button className="btn btn-gold pwa-install-cta" onClick={handleInstall}>
        Instalar
      </button>
      <button className="pwa-install-dismiss" onClick={handleDismiss} aria-label="No mostrar de nuevo">
        ✕
      </button>
    </div>
  );
}
